import { RouteGroupItemWaypointsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/types/route-group-item-waypoints.enum';
import { Empty, Table } from 'antd';
import { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetWaypointPictures } from '@/features/service-history/api/use-get-waypoint-pictures';
import { usePagination } from '@/hooks/use-pagination';
import { useRowExpand } from '@/hooks/use-row-expand';
import { useUserInfoStore } from '@/store';

import { useGetWaypoints } from '../api/use-get-waypoints';
import { useWaypointsTable } from '../hooks/use-waypoint-table';
import { WaypointStatus } from '../types/waypoint';

type Props = {
  contractNumber: string;
  status: WaypointStatus;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  garbageId?: number;
};

const WaypointsTable = ({ contractNumber, status, dateRange, garbageId }: Props) => {
  const { t } = useTranslation('serviceHistoryPage');
  const { selectedAccount } = useUserInfoStore();
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { handleExpand, expandedRowKeys } = useRowExpand({ allowMultiple: false });

  const getFiltersByStatus = (status: WaypointStatus) => {
    switch (status) {
      case WaypointStatus.Discharged:
        return {
          isDone: true,
          isFailed: false,
        };

      case WaypointStatus.Fail:
        return {
          isDone: true,
          isFailed: true,
        };

      case WaypointStatus.NotDone:
        return {
          isDone: false,
          isFailed: false,
        };

      default:
        break;
    }
  };

  const filters = useMemo(() => getFiltersByStatus(status), [status]);

  const waypointsQuery = useGetWaypoints({
    params: {
      contractNumbers: [contractNumber],
      ownerCodes: [selectedAccount?.code],
      ...filters,
      ...(dateRange?.[0] && { startDate: dateRange[0].toISOString() }),
      ...(dateRange?.[1] && { endDate: dateRange[1].toISOString() }),
      sideloading: [
        RouteGroupItemWaypointsSideloading.Owner,
        RouteGroupItemWaypointsSideloading.Object,
        RouteGroupItemWaypointsSideloading.ObjectItem,
        RouteGroupItemWaypointsSideloading.Garbage,
        RouteGroupItemWaypointsSideloading.ItemType,
        RouteGroupItemWaypointsSideloading.WaypointLogDetails,
        RouteGroupItemWaypointsSideloading.Kojv,
        RouteGroupItemWaypointsSideloading.KojvService,
        RouteGroupItemWaypointsSideloading.RouteGroupItem,
        RouteGroupItemWaypointsSideloading.ObjectAddress,
        RouteGroupItemWaypointsSideloading.ObjectItemDischarge,
        RouteGroupItemWaypointsSideloading.Price,
        RouteGroupItemWaypointsSideloading.ExtraServicePrice,
      ],
      pageSize,
      page,
      garbageIds: [garbageId],
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });

  const waypointIds = waypointsQuery.data?.data?.map((waypoint) => waypoint.id);
  const waypointPhotosQuery = useGetWaypointPictures({
    params: {
      waypointIds,
    },
    queryConfig: {
      enabled: !!waypointIds?.length,
    },
  });

  const { columns, expandedRowRender } = useWaypointsTable({
    waypointPhotosQuery: {
      data: waypointPhotosQuery.data,
      isLoading: waypointPhotosQuery.isLoading,
    },
    handleExpand,
    expandedRowKeys,
  });

  return (
    <Table
      onRow={(record) => ({
        onClick: (event) => handleExpand(record.id.toString(), event),
      })}
      rowClassName={(record) => (expandedRowKeys.includes(record.id.toString()) ? 'bg-[#F9FAE0] cursor-pointer' : ' cursor-pointer')}
      rowKey={(record) => record.id.toString()}
      className="w-full"
      loading={waypointsQuery.isLoading}
      locale={{
        emptyText: <Empty description={t('noData', { ns: 'table' })} />,
      }}
      expandable={{
        expandedRowClassName: () => 'bg-[#F9FAE0] !mx-0 !px-0 [&_.ant-table-cell]:!p-0',
        expandedRowKeys,
        expandedRowRender,
        rowExpandable: () => true,
        showExpandColumn: false,
      }}
      columns={columns}
      dataSource={waypointsQuery?.data?.data || []}
      pagination={{
        defaultCurrent: page,
        hideOnSinglePage: true,
        pageSizeOptions: [10, 20, 50, 100],
        total: waypointsQuery.data?.meta?.totalItems,
        position: ['bottomRight'],
        onChange: setPage,
        onShowSizeChange: (_, size) => setPageSize(size),
        defaultPageSize: pageSize,
      }}
    />
  );
};

export default WaypointsTable;
